import {
    ListPage,
    listRoutePrefetch,
    useListQuery,
    useListState,
    type FilterControlOptions,
    type ListConfig,
    type ListContract,
    type ListPageProps,
    type Prefetch,
} from '@bemor/web-kit';
import type { ListFilterDef, ListViewPreset } from '@bemor/web-kit/ui';
import type { ReactNode } from 'react';

import { LISTS } from '@/api/generated/lists';

/** Admin's saved views route (staff; `ListView` in the contract). */
export const VIEWS_ENDPOINT = '/staff/list-views' as const;

export type ListPath = keyof typeof LISTS;

/** A list's contract (its sorts, date column, filters and export, generated from platform). */
export const contractOf = (path: ListPath) =>
    LISTS[path] as unknown as ListContract;

type AdminListOptions<Row> = Omit<
    ListConfig<Row>,
    'contract' | 'viewsEndpoint'
>;

/**
 * BM-46 §2.4: one list page's state (the kit's `useListState`: views, filters, sorting, paging in the URL) and its
 * rows (`useListQuery`, the same key the route's prefetch seeds on the server).
 */
export function useAdminList<Row>(
    pageKey: string,
    path: ListPath,
    options: AdminListOptions<Row>,
    enabled = true,
) {
    const state = useListState<Row>(pageKey, {
        contract: contractOf(path),
        viewsEndpoint: VIEWS_ENDPOINT,
        ...options,
    });
    const query = useListQuery<Row>(state, enabled);
    return { state, query };
}

/**
 * A list route's prefetch: the person's views of the page, then the rows of the view they land on, with `__me__`
 * resolved to the signed-in staff member (BM-46 §2.0 + §2.4). `pathOf` builds the concrete path of a contract with
 * parameters (`/staff/queues/{queue}`).
 */
export function adminListPrefetch(
    pageKey: string,
    path: ListPath,
    options: {
        presets?: readonly ListViewPreset[];
        pathOf?: (params: Record<string, string | undefined>) => string;
    } = {},
): Prefetch {
    return listRoutePrefetch({
        contract: contractOf(path),
        viewsEndpoint: VIEWS_ENDPOINT,
        pageKey,
        presets: options.presets,
        pathOf: options.pathOf,
    });
}

/** The filters of a page: the given labels, kept only where the endpoint has the filter (the contract decides). */
export function filterDefs(
    path: ListPath,
    labels: Record<string, string>,
): ListFilterDef[] {
    const contract = contractOf(path);
    const defs: ListFilterDef[] = [];
    for (const [key, label] of Object.entries(labels)) {
        if (key === 'range') {
            if (contract.date)
                defs.push({ key, label, params: ['range', 'from', 'to'] });
        } else if (key in contract.filters) defs.push({ key, label });
    }
    return defs;
}

/**
 * The kit's `ListPage` with the page's filters only: the kit builds a control for every filter of the contract;
 * Admin offers the ones the page lists (`filterDefs`), labelled as there, and drops the rest (an id-only filter is
 * reached from a record's page, not typed).
 */
export function AdminListPage<Row>({
    controls = {},
    ...props
}: ListPageProps<Row>) {
    const { state } = props;
    const defs = state.config.filters ?? [];
    const labels = Object.fromEntries(defs.map((f) => [f.key, f.label]));
    const all: Record<string, FilterControlOptions | ReactNode | null> = {};
    const keys = [
        ...Object.keys(state.config.contract.filters),
        ...(state.config.contract.date ? ['range'] : []),
    ];
    for (const key of keys)
        all[key] = key in labels ? { label: labels[key] } : null;
    for (const [key, given] of Object.entries(controls))
        all[key] =
            given !== null &&
            typeof given === 'object' &&
            !Array.isArray(given) &&
            !('$$typeof' in (given as object))
                ? { label: labels[key], ...(given as FilterControlOptions) }
                : given;
    return <ListPage {...props} controls={all} />;
}
